import { create } from "zustand";
import { persist } from "zustand/middleware";

export type CartItem = {
  variantId: string;
  variantSlug: string;
  productTitle: string;
  variantTitle: string;
  priceCents: number;
  currency: string;
  quantity: number;
  image?: string;
};

type Fulfillment = { mode: "pickup" | "ship"; detail?: string };

type CartState = {
  items: CartItem[];
  fulfillment: Fulfillment;
  addItem: (item: Omit<CartItem, "quantity">, quantity?: number) => void;
  removeItem: (variantId: string) => void;
  updateQuantity: (variantId: string, quantity: number) => void;
  clearCart: () => void;
  setFulfillment: (mode: Fulfillment["mode"], detail?: string) => void;
  totalItems: () => number;
  totalCents: () => number;
};

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      fulfillment: { mode: "ship" },

      addItem: (item, quantity = 1) => {
        set((state) => {
          const existingIndex = state.items.findIndex(
            (i) => i.variantId === item.variantId,
          );

          if (existingIndex >= 0) {
            const updated = [...state.items];
            updated[existingIndex].quantity += quantity;
            return { items: updated };
          }

          return { items: [...state.items, { ...item, quantity }] };
        });
      },

      removeItem: (variantId) => {
        set((state) => ({
          items: state.items.filter((i) => i.variantId !== variantId),
        }));
      },

      updateQuantity: (variantId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(variantId);
          return;
        }
        set((state) => {
          const updated = state.items.map((item) =>
            item.variantId === variantId ? { ...item, quantity } : item,
          );
          return { items: updated };
        });
      },

      clearCart: () => set({ items: [] }),

      setFulfillment: (mode, detail) => {
        set({ fulfillment: { mode, detail } });
      },

      totalItems: () => {
        return get().items.reduce((sum, item) => sum + item.quantity, 0);
      },

      totalCents: () => {
        return get().items.reduce(
          (sum, item) => sum + item.priceCents * item.quantity,
          0,
        );
      },
    }),
    {
      name: "eg-cart",
      // Avoid SSR hydration mismatches: first render uses the initial state on both
      // server and client; persisted state is loaded after mount via rehydrate().
      skipHydration: true,
    },
  ),
);
